import React, { useState, useEffect, useCallback, ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation, Trans } from 'react-i18next'
import type { Session } from '@/types'
import { supabase } from '../../../lib/supabase'
import { ensureProfileExists } from '../../../lib/profileUtils'
import { validateNotSelfShare, validateEmailExists, findProfileByEmail, type Profile } from '../../../utils/biblionautasValidation'
import CustomConfirmModal from '../../common/CustomConfirmModal'
import HistoryTable from './HistoryTable'
import SharedSettings from './SharedSettings'
import styles from './ShareAccessSection.module.css'

type ShareStatus = 'pending' | 'accepted' | 'rejected'
type ShareAction = 'delete' | 'accept' | 'reject'

interface ViewerProfile {
    email?: string
    full_name?: string | null
    bio?: string | null
}

interface LibraryShare {
    id: string
    owner_id: string
    viewer_id: string
    info_shared_to_email: string
    status: ShareStatus
    message: string
    created_at: string
    viewer_profile?: ViewerProfile | ViewerProfile[]
}

interface NotificationRequest extends Partial<LibraryShare> {
    type: 'request' | 'forgotten'
}

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: 'success' | 'danger' | 'warning' | 'info'
}

interface ShareAccessSectionProps {
    session: Session
}

export default function ShareAccessSection({ session }: Readonly<ShareAccessSectionProps>) {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [shares, setShares] = useState<LibraryShare[]>([])
    const [newRequests, setNewRequests] = useState<NotificationRequest[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedShare, setSelectedShare] = useState<LibraryShare | null>(null)
    const [modalAction, setModalAction] = useState<ShareAction | null>(null)
    const [lastSeen, setLastSeen] = useState<string | null>(null)
    const [replyMessages, setReplyMessages] = useState<Record<string, string>>({})
    const [showSettingsModal, setShowSettingsModal] = useState(false)

    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const fetchSettings = useCallback(async () => {
        try {
            const { data } = await supabase
                .from('shared_library_settings')
                .select('last_seen_shares_at')
                .eq('user_id', session.user.id)
                .single()
            setLastSeen(data?.last_seen_shares_at || '1970-01-01T00:00:00Z')
        } catch (error) {
            console.error('Error fetching settings:', error)
        }
    }, [session.user.id])

    const fetchShares = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('library_shares')
                .select('*, viewer_profile:viewer_id(email, full_name, bio)')
                .eq('owner_id', session.user.id)
                .neq('status', 'rejected')
                .order('created_at', { ascending: false })

            if (error) throw error
            setShares((data as LibraryShare[]) || [])
        } catch (error) {
            console.error('Error fetching shares:', error)
        } finally {
            setLoading(false)
        }
    }, [session.user.id])

    useEffect(() => {
        const init = async () => {
            await fetchSettings()
            await fetchShares()
        }
        init()
    }, [fetchSettings, fetchShares])

    useEffect(() => {
        const fetchNotifications = async () => {
            if (!lastSeen) return

            const newReqs = shares.filter((s) => new Date(s.created_at) > new Date(lastSeen) && s.status === 'pending')

            try {
                const { data: forgottenEvents } = await supabase
                    .from('library_share_history')
                    .select('*')
                    .eq('owner_id', session.user.id)
                    .eq('action', 'forgotten')
                    .gt('created_at', lastSeen)

                const formattedReqs: NotificationRequest[] = newReqs.map((r) => ({ ...r, type: 'request' }))
                const formattedForgotten: NotificationRequest[] = (forgottenEvents || []).map((f) => ({
                    id: f.id,
                    info_shared_to_email: f.target_email,
                    message: f.message,
                    type: 'forgotten',
                    created_at: f.created_at,
                }))

                setNewRequests([...formattedReqs, ...formattedForgotten].sort((a, b) => new Date(b.created_at ?? '').getTime() - new Date(a.created_at ?? '').getTime()))
            } catch (err) {
                console.error('Error fetching forgotten events', err)
                const formattedReqs: NotificationRequest[] = newReqs.map((r) => ({ ...r, type: 'request' }))
                setNewRequests(formattedReqs)
            }
        }

        if (lastSeen) {
            fetchNotifications()
        }
    }, [shares, lastSeen, session.user.id])

    const logHistory = async (action: string, share: LibraryShare, customMsg: string | null = null) => {
        let historyMessage = customMsg
        historyMessage ??= action === 'granted' ? share.message : ''

        try {
            await supabase.from('library_share_history').insert({
                owner_id: session.user.id,
                biblionaut_id: share.viewer_id,
                target_email: share.info_shared_to_email,
                action: action,
                message: historyMessage,
            })
        } catch (err) {
            console.error('Error logging history:', err)
        }
    }

    const handleGrantAccess = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!email) return

        // Validate: cannot share with self
        const selfCheck = validateNotSelfShare(email, session.user.email || '')
        if (!selfCheck.isValid) {
            showNotify('Error', selfCheck.error!, 'danger')
            return
        }

        try {
            const profileReady = await ensureProfileExists(session)
            if (!profileReady) {
                showNotify('Error', t('biblionautas.share.notifies.profileError'), 'danger')
                return
            }

            // Fetch all profiles to validate email exists
            const { data: allProfiles } = await supabase
                .from('profiles')
                .select('id, email, full_name')

            const profiles = (allProfiles || []) as Profile[]

            // Validate: email must be registered
            const emailCheck = validateEmailExists(email, profiles)
            if (!emailCheck.isValid) {
                showNotify('Error', emailCheck.error!, 'danger')
                return
            }

            // Find the viewer profile
            const viewerProfile = findProfileByEmail(email, profiles)
            if (!viewerProfile) {
                showNotify('Error', t('biblionautas.share.notifies.notFound'), 'danger')
                return
            }

            const viewerId = viewerProfile.id

            const { data: newShare, error } = await supabase
                .from('library_shares')
                .upsert(
                    {
                        owner_id: session.user.id,
                        info_shared_to_email: email,
                        viewer_id: viewerId,
                        status: 'accepted',
                        message: message,
                    },
                    { onConflict: 'owner_id, info_shared_to_email' }
                )
                .select()
                .single()

            if (error) throw error

            if (newShare) {
                await logHistory('granted', newShare as LibraryShare, message)
            }

            setEmail('')
            setMessage('')
            fetchShares()
            setEmail('')
            setMessage('')
            fetchShares()
            showNotify('Éxito', t('biblionautas.share.notifies.success'), 'success')
        } catch (error) {
            console.error('Error granting access:', error)
            showNotify('Error', t('biblionautas.share.notifies.error'), 'danger')
        }
    }

    const handleReplyChange = (shareId: string, val: string) => {
        setReplyMessages((prev) => ({ ...prev, [shareId]: val }))
    }

    const handleActionClick = (share: LibraryShare, action: ShareAction) => {
        setSelectedShare(share)
        setModalAction(action)
        setModalOpen(true)
    }

    const handleModalConfirm = async () => {
        if (!selectedShare) return

        const optionalMessage = replyMessages[selectedShare.id] || ''

        try {
            if (modalAction === 'delete') {
                await supabase.from('library_shares').delete().eq('id', selectedShare.id)
                await logHistory('removed', selectedShare, t('biblionautas.share.notifies.revoked'))
            } else if (modalAction === 'accept') {
                await supabase.from('library_shares').update({ status: 'accepted' }).eq('id', selectedShare.id)
                await logHistory('accepted', selectedShare, optionalMessage)
            } else if (modalAction === 'reject') {
                await supabase.from('library_shares').update({ status: 'rejected' }).eq('id', selectedShare.id)
                await logHistory('rejected', selectedShare, optionalMessage)
            }

            setModalOpen(false)
            setReplyMessages((prev) => {
                const newMap = { ...prev }
                delete newMap[selectedShare.id]
                return newMap
            })
            fetchShares()
        } catch (error) {
            console.error('Error performing action:', error)
            showNotify('Error', t('biblionautas.share.notifies.genericError'), 'danger')
        }
    }

    const pendingShares = shares.filter((s) => s.status === 'pending')
    const acceptedShares = shares.filter((s) => s.status === 'accepted')

    const getProfileData = (share: LibraryShare): ViewerProfile => {
        const profile = Array.isArray(share.viewer_profile) ? share.viewer_profile[0] : share.viewer_profile
        return profile || {}
    }

    return (
        <div className={`${styles.biblioSection} fade-in`}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid var(--border-color)',
                    paddingBottom: '1rem',
                }}
            >
                <h3 style={{ margin: 0, border: 'none', padding: 0 }}>{t('biblionautas.share.title')}</h3>
                <button
                    onClick={() => setShowSettingsModal(true)}
                    className="btn-primary"
                    style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.85rem',
                        backgroundColor: 'transparent',
                        color: 'var(--color-primary)',
                        border: '1px solid var(--color-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                    data-testid="privacy-settings-btn"
                >
                    ⚙️ {t('biblionautas.share.privacySettings')}
                </button>
            </div>

            {/* Notifications Block */}
            {newRequests.length > 0 && (
                <div className={styles.notificationsBlock}>
                    <h4>{t('biblionautas.share.notifications.title')}</h4>
                    {newRequests.map((req) => (
                        <div key={req.id} className={styles.notificationItem}>
                            {req.type === 'forgotten' ? (
                                <div>
                                    <Trans
                                        i18nKey="biblionautas.share.notifications.forgotten"
                                        values={{ email: req.info_shared_to_email }}
                                        components={{ strong: <strong /> }}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <Trans
                                        i18nKey="biblionautas.share.notifications.request"
                                        values={{ email: req.info_shared_to_email }}
                                        components={{ strong: <strong /> }}
                                    />
                                </div>
                            )}
                            {req.message && <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>"{req.message}"</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Grant Access Form */}
            <div className={styles.accessFormCard} data-testid="share-access-form">
                <h4>{t('biblionautas.share.grant.title')}</h4>
                <form onSubmit={handleGrantAccess} className={styles.biblioForm}>
                    <div className={styles.formGroup}>
                        <label>{t('biblionautas.share.grant.emailLabel')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            placeholder={t('biblionautas.share.grant.emailPlaceholder')}
                            required
                            data-testid="share-email-input"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>{t('biblionautas.share.grant.messageLabel')}</label>
                        <textarea
                            value={message}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                            placeholder={t('biblionautas.share.grant.messagePlaceholder')}
                            data-testid="share-message-input"
                        />
                    </div>
                    <button type="submit" className="btn-primary" data-testid="share-submit-btn">
                        {t('biblionautas.share.grant.submit')}
                    </button>
                </form>
            </div>

            {/* Pending Requests List */}
            {pendingShares.length > 0 && (
                <div className="shares-list" data-testid="pending-shares-list">
                    <h4 data-testid="pending-requests-title">{t('biblionautas.share.pending.title')}</h4>
                    <div className={styles.shareCardsGrid}>
                        {pendingShares.map((share) => {
                            const profile = getProfileData(share)
                            return (
                                <div key={share.id} className={styles.shareCard} data-testid={`pending-share-card-${share.info_shared_to_email}`}>
                                    <div className={styles.shareHeader}>
                                        <div className="share-user-info">
                                            <span className="share-name" style={{ fontWeight: 'bold', display: 'block' }}>
                                                {profile.full_name || 'Usuario'}
                                            </span>
                                            <span className={styles.shareEmail} style={{ fontSize: '0.85em', color: '#666' }}>
                                                {profile.email || share.info_shared_to_email}
                                            </span>
                                        </div>
                                    </div>
                                    {profile.bio && (
                                        <div className="share-bio" title={profile.bio}>
                                            {profile.bio}
                                        </div>
                                    )}
                                    <p className={styles.shareMessage}>"{share.message}"</p>

                                    {/* Inline Reply Input */}
                                    <div className="share-reply-input">
                                        <input
                                            type="text"
                                            placeholder={t('biblionautas.share.pending.replyPlaceholder')}
                                            value={replyMessages[share.id] || ''}
                                            onChange={(e) => handleReplyChange(share.id, e.target.value)}
                                            className="inline-input"
                                            data-testid={`reply-share-input-${share.id}`}
                                        />
                                    </div>

                                    <div className={styles.shareActions}>
                                        <button onClick={() => handleActionClick(share, 'accept')} className={styles.btnIcon} title={t('biblionautas.share.pending.accept')} data-testid={`accept-share-btn-${share.id}`}>
                                            👍
                                        </button>
                                        <button onClick={() => handleActionClick(share, 'reject')} className={styles.btnIcon} title={t('biblionautas.share.pending.reject')} data-testid={`reject-share-btn-${share.id}`}>
                                            👎
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Accepted Users List */}
            <div className="shares-list" style={{ marginTop: '2rem' }} data-testid="accepted-shares-list">
                <h4 data-testid="accepted-users-title">{t('biblionautas.share.accepted.title')}</h4>
                {(() => {
                    if (loading) return <p>Cargando...</p>
                    if (acceptedShares.length === 0) return <p className="empty-state" data-testid="accepted-shares-empty">{t('biblionautas.share.accepted.empty')}</p>
                    return (
                        <div className={styles.shareCardsGrid}>
                            {acceptedShares.map((share) => {
                                const profile = getProfileData(share)
                                return (
                                    <div key={share.id} className={styles.shareCard} data-testid={`accepted-share-card-${share.info_shared_to_email}`}>
                                        <div className={styles.shareHeader}>
                                            <div className="share-user-info">
                                                <span className="share-name" style={{ fontWeight: 'bold', display: 'block' }}>
                                                    {profile.full_name || 'Usuario'}
                                                </span>
                                                <span className={styles.shareEmail} style={{ fontSize: '0.85em', color: '#666' }} data-testid="share-email">
                                                    {profile.email || share.info_shared_to_email}
                                                </span>
                                            </div>
                                        </div>
                                        {profile.bio && (
                                            <div className="share-bio" title={profile.bio}>
                                                {profile.bio}
                                            </div>
                                        )}
                                        <div className={styles.shareActions}>
                                            <button onClick={() => handleActionClick(share, 'delete')} className={styles.btnIcon} title={t('biblionautas.share.accepted.revoke')} data-testid={`revoke-share-btn-${share.id}`}>
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                })()}
            </div>

            {/* History Table */}
            <div className="section-divider"></div>
            <HistoryTable session={session} key={shares.length} />

            <CustomConfirmModal
                isOpen={modalOpen}
                type={modalAction === 'delete' || modalAction === 'reject' ? 'danger' : 'confirm'}
                title={(() => {
                    if (modalAction === 'delete') return t('biblionautas.share.modals.deleteTitle')
                    if (modalAction === 'accept') return t('biblionautas.share.modals.acceptTitle')
                    return t('biblionautas.share.modals.rejectTitle')
                })()}
                message={(() => {
                    if (modalAction === 'delete') return t('biblionautas.share.modals.deleteMessage')
                    if (modalAction === 'accept') return t('biblionautas.share.modals.acceptMessage')
                    return t('biblionautas.share.modals.rejectMessage')
                })()}
                confirmText={t('biblionautas.share.modals.confirm')}
                onConfirm={handleModalConfirm}
                onCancel={() => setModalOpen(false)}
                data-testid="share-confirm-modal"
            />

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })} 
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
            {showSettingsModal &&
                createPortal(
                    <div className={styles.modalOverlay} onClick={() => setShowSettingsModal(false)} role="presentation">
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
                            <div
                                className="modal-header"
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}
                            >
                                <h3 style={{ margin: 0 }}>{t('biblionautas.share.modals.preferencesTitle')}</h3>
                                <button onClick={() => setShowSettingsModal(false)} className={styles.btnIcon} style={{ fontSize: '1.5rem' }}>
                                    ×
                                </button>
                            </div>
                            <SharedSettings session={session} />
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
